import { trackDocumentGenerated, trackDocumentGenerationStarted } from '@/lib/analytics';
import { setTurnstileToken as storeTurnstileToken } from '@/lib/turnstile-token-manager';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LegalDocument } from '@/schemas/schema';
import { generateDocument } from '@/modules/templates/services/ai.service';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { saveTemplateReview } from '../../../review/TemplateReviewStorage';
import { useDynamicForm } from '../DynamicFormContext';

export interface GenerationStage {
  title: string;
  description: string;
  progress: number;
  duration: number;
}

export interface UseGenerationFlowReturn {
  isGenerating: boolean;
  generationError: string | null;
  setGenerationError: (error: string | null) => void;
  isTokenExpired: boolean;
  retryTurnstileToken: string | null;
  setRetryTurnstileToken: (token: string | null) => void;
  retryTurnstileStatus: 'success' | 'error' | 'expired' | 'required';
  setRetryTurnstileStatus: (status: 'success' | 'error' | 'expired' | 'required') => void;
  fakeProgress: number;
  displayedProgress: number;
  generationStepIndex: number;
  generationSteps: GenerationStage[];
  generationTips: string[];
  currentTipIndex: number;
  currentStage: GenerationStage;
  handleSubmit: () => Promise<void>;
  retryGeneration: (newToken: string) => Promise<void>;
  handleRetryGeneration: () => void;
}

export function useGenerationFlow(
  turnstileToken: string | null,
  markDraftCompleted?: (documentJson: string) => Promise<void>,
  draftId?: string | null
): UseGenerationFlowReturn {
  const router = useNavigate();
  const { t } = useTranslation();
  const { config, formData, setSubmitting, canProceed } = useDynamicForm();

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStepIndex, setGenerationStepIndex] = useState(0);
  const [fakeProgress, setFakeProgress] = useState(0);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [retryTurnstileToken, setRetryTurnstileToken] = useState<string | null>(null);
  const [retryTurnstileStatus, setRetryTurnstileStatus] = useState<
    'success' | 'error' | 'expired' | 'required'
  >('required');
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const isNavigatingRef = useRef(false);

  const generationSteps: GenerationStage[] = useMemo(
    () => [
      {
        title: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_ANALYZING_TITLE'),
        description: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_ANALYZING_DESCRIPTION'),
        progress: 15,
        duration: 2500,
      },
      {
        title: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_DRAFTING_TITLE'),
        description: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_DRAFTING_DESCRIPTION'),
        progress: 40,
        duration: 3000,
      },
      {
        title: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_BUILDING_TITLE'),
        description: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_BUILDING_DESCRIPTION'),
        progress: 65,
        duration: 3500,
      },
      {
        title: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_FINALIZING_TITLE'),
        description: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_FINALIZING_DESCRIPTION'),
        progress: 85,
        duration: 2500,
      },
      {
        title: t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_QUALITYASSURANCE_TITLE'),
        description: t(
          'EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STAGES_QUALITYASSURANCE_DESCRIPTION'
        ),
        progress: 95,
        duration: 3000,
      },
    ],
    [t]
  );

  const generationTips = useMemo(
    () => [
      t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TIPS_TIP1'),
      t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TIPS_TIP2'),
      t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TIPS_TIP3'),
      t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TIPS_TIP4'),
      t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TIPS_TIP5'),
    ],
    [t]
  );

  // Animate through generation steps
  useEffect(() => {
    if (!isGenerating || generationSteps.length === 0) {
      setGenerationStepIndex(0);
      setFakeProgress(0);
      return;
    }

    type Timer = ReturnType<typeof setTimeout>;
    let isActive = true;
    const timeouts: Timer[] = [];
    let idleInterval: ReturnType<typeof setInterval> | null = null;

    setGenerationStepIndex(0);
    setFakeProgress(generationSteps[0].progress);

    let cumulativeDelay = 0;
    for (let index = 1; index < generationSteps.length; index++) {
      cumulativeDelay += generationSteps[index - 1].duration;
      const timeout = setTimeout(() => {
        if (!isActive) return;
        setGenerationStepIndex(index);
        setFakeProgress(generationSteps[index].progress);
      }, cumulativeDelay);
      timeouts.push(timeout);
    }

    // After all steps, slowly increment progress to 99
    cumulativeDelay += generationSteps[generationSteps.length - 1].duration;
    const idleTimeout = setTimeout(() => {
      idleInterval = setInterval(() => {
        if (!isActive) return;
        setFakeProgress((prev) => {
          const next = prev + 0.4 + Math.random() * 1.1;
          return next >= 99 ? 99 : next;
        });
      }, 2000);
    }, cumulativeDelay);
    timeouts.push(idleTimeout);

    return () => {
      isActive = false;
      timeouts.forEach((timeout) => clearTimeout(timeout));
      if (idleInterval) clearInterval(idleInterval);
    };
  }, [isGenerating, generationSteps]);

  // Rotate tips during generation
  useEffect(() => {
    if (!isGenerating) return;
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % generationTips.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isGenerating, generationTips.length]);

  // Shared generation logic — calls OpenRouter directly
  const callGenerateApi = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (_token: string) => {
      if (!config) return;

      const result = await generateDocument({
        templateTitle: config.title,
        templateSlug: config.slug,
        systemPrompt: config.systemPrompt,
        systemPromptRole: config.systemPromptRole,
        formData,
      });

      return result;
    },
    [config, formData]
  );

  const navigateToReview = useCallback(
    async (result: { document: LegalDocument }) => {
      if (!config) return;

      trackDocumentGenerated(config.slug, config.title);

      if (markDraftCompleted) {
        try {
          await markDraftCompleted(JSON.stringify(result.document));
        } catch (err) {
          console.warn('Failed to mark draft as completed:', err);
        }
      }

      const reviewData = {
        document: result.document,
        formData: formData,
        templateId: config.id,
        templateSlug: config.slug,
        templateTitle: config.title,
        storedAt: new Date().toISOString(),
      };

      saveTemplateReview(config.slug, reviewData);

      setFakeProgress(100);
      isNavigatingRef.current = true;

      await new Promise((resolve) => setTimeout(resolve, 500));
      // Carry the document id forward so the review page can look up payment
      // status (guest paywall) and resume editing the same document row.
      const reviewUrl = draftId
        ? `/templates/${config.slug}/review?document=${encodeURIComponent(draftId)}`
        : `/templates/${config.slug}/review`;
      router(reviewUrl);
    },
    [config, formData, router, markDraftCompleted, draftId]
  );

  const startGeneration = useCallback(() => {
    setSubmitting(true);
    setIsGenerating(true);
    setGenerationStepIndex(0);
    setFakeProgress(0);
    setGenerationError(null);
    setIsTokenExpired(false);
  }, [setSubmitting]);

  const handleSubmit = useCallback(async () => {
    if (!canProceed() || !turnstileToken || !config) return;

    trackDocumentGenerationStarted(config.slug, config.title);
    startGeneration();

    try {
      const result = await callGenerateApi(turnstileToken);
      if (result) {
        await navigateToReview(result);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to generate document. Please try again.';

      if (!isTokenExpired) {
        setGenerationError(errorMessage);
        setIsGenerating(false);
        setSubmitting(false);
      }
    }
  }, [
    canProceed,
    turnstileToken,
    config,
    startGeneration,
    callGenerateApi,
    navigateToReview,
    isTokenExpired,
    setSubmitting,
  ]);

  const retryGeneration = useCallback(
    async (newToken: string) => {
      if (!config) return;

      setRetryTurnstileToken(newToken);
      setRetryTurnstileStatus('success');
      setIsTokenExpired(false);

      storeTurnstileToken(newToken);
      await new Promise((resolve) => setTimeout(resolve, 100));

      startGeneration();

      try {
        const result = await callGenerateApi(newToken);
        if (result) {
          await navigateToReview(result);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to generate document. Please try again.';
        setGenerationError(errorMessage);
        setIsGenerating(false);
        setSubmitting(false);
        setIsTokenExpired(false);
      }
    },
    [config, startGeneration, callGenerateApi, navigateToReview, setSubmitting]
  );

  const handleRetryGeneration = useCallback(() => {
    setGenerationError(null);
    setIsTokenExpired(false);
    setIsGenerating(false);
    setSubmitting(false);
    setFakeProgress(0);
    setGenerationStepIndex(0);
    setRetryTurnstileToken(null);
    setRetryTurnstileStatus('required');
  }, [setSubmitting]);

  const displayedProgress = Math.round(fakeProgress);
  const currentStage = generationSteps[Math.min(generationStepIndex, generationSteps.length - 1)];

  return {
    isGenerating,
    generationError,
    setGenerationError,
    isTokenExpired,
    retryTurnstileToken,
    setRetryTurnstileToken,
    retryTurnstileStatus,
    setRetryTurnstileStatus,
    fakeProgress,
    displayedProgress,
    generationStepIndex,
    generationSteps,
    generationTips,
    currentTipIndex,
    currentStage,
    handleSubmit,
    retryGeneration,
    handleRetryGeneration,
  };
}
